// ✅ ALTERED: FeedbackFormFill UI to match your portal theme (Orange header + blue borders)
// - Outer Paper: borderRadius 2 + blue border + overflow hidden
// - Orange gradient header bar (Title + Description)
// - Body BG = #F2F2F2
// - Surfaces: white + outlined blue borders
// - Inputs: consistent outlined borders + focus orange
// - Question cards: left orange accent stripe + zebra tint + hover
// - Buttons: theme orange primary
// NOTE: logic unchanged (only UI/theme + small layout polish)

import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
  Rating,
  TextField,
  Divider,
  Stack,
} from "@mui/material";
import { API_URL } from "./config";
import { useParams, useNavigate } from "react-router-dom";

export default function FeedbackFormFill() {
  // ✅ Theme constants (same as your Builder page)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const { id } = useParams();
  const nav = useNavigate();

  const [form, setForm] = React.useState(null);
  const [questions, setQuestions] = React.useState([]);
  const [answers, setAnswers] = React.useState({}); // keyed by QuestionID
  const [overall, setOverall] = React.useState(0);
  const [details, setDetails] = React.useState("");
  const [anonymous, setAnonymous] = React.useState(false);

  const [err, setErr] = React.useState("");
  const [msg, setMsg] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("auth");
        const resp = await axios.get(`${API_URL}/api/feedback/forms/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setForm(resp.data.form);
        setQuestions(resp.data.questions || []);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load form");
      }
    })();
  }, [id]);

  const setAnswer = (qid, value) => {
    setAnswers((p) => ({ ...p, [qid]: value }));
  };

  const submit = async () => {
    setErr("");
    setMsg("");
    const token = localStorage.getItem("auth");
    if (!token) return setErr("Session expired. Please login again.");

    // required validation
    for (const q of questions) {
      if (q.IsRequired) {
        const v = answers[q.QuestionID];
        if (v == null || v === "" || (q.QuestionType === "rating" && !Number(v))) {
          return setErr("Please answer all required questions.");
        }
      }
    }

    const Answers = questions
      .map((q) => {
        const v = answers[q.QuestionID];
        if (v == null || v === "") return null;

        if (q.QuestionType === "rating") {
          return { QuestionID: q.QuestionID, AnswerRating: Number(v) };
        }
        if (q.QuestionType === "mcq") {
          return { QuestionID: q.QuestionID, AnswerOptionID: Number(v) };
        }
        return { QuestionID: q.QuestionID, AnswerText: String(v) };
      })
      .filter(Boolean);

    try {
      await axios.post(
        `${API_URL}/api/feedback/forms/${id}/submit`,
        {
          IsAnonymous: anonymous ? 1 : 0,
          OverallRating: form?.AllowOverallRating ? overall : null,
          AdditionalDetails: details || null,
          Answers,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setMsg("Thank you! Feedback submitted.");
      setTimeout(() => nav("/feedback/forms"), 800);
    } catch (e) {
      setErr(e?.response?.data?.message || "Submit failed");
    }
  };

  // ✅ common sx helpers
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(25,118,210,0.45)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(240,120,40,0.75)",
    },
  };

  const questionCardSx = (i) => ({
    ...surfacePaperSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    backgroundColor: i % 2 ? ROW_ALT : "white",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
    "&:hover": { backgroundColor: "rgba(240,120,40,0.04)" },
  });

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const ratingSx = {
    "& .MuiRating-iconFilled": { color: THEME_ORANGE },
    "& .MuiRating-iconHover": { color: THEME_ORANGE_2 },
  };

  if (!form) {
    return (
      <Box sx={{ p: 2, backgroundColor: BG }}>
        <Paper
          elevation={5}
          sx={{
            maxWidth: 900,
            mx: "auto",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(25,118,210,0.15)",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
              color: "white",
            }}
          >
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Feedback
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Loading form...
            </Typography>
          </Box>

          <Box sx={{ p: 2, backgroundColor: BG }}>
            {err ? <Alert severity="error">{err}</Alert> : <Typography>Loading...</Typography>}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 800,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={form.Title}
            >
              {form.Title}
            </Typography>
            {form.Description ? (
              <Typography
                sx={{
                  opacity: 0.9,
                  fontSize: "0.85rem",
                  mt: 0.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={form.Description}
              >
                {form.Description}
              </Typography>
            ) : null}
          </Box>

          <Button variant="contained" onClick={submit} sx={primaryBtnSx}>
            Submit
          </Button>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {msg && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {msg}
            </Alert>
          )}
          {err && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {err}
            </Alert>
          )}

          {/* Overall rating */}
          {form.AllowOverallRating ? (
            <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, mb: 2 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 0.5 }}>
                {form.OverallRatingLabel || "Overall rating"}
              </Typography>
              <Rating
                sx={ratingSx}
                value={overall}
                onChange={(_, v) => setOverall(v || 0)}
              />
            </Paper>
          ) : null}

          {/* Questions */}
          <Stack spacing={2}>
            {questions.map((q, i) => (
              <Paper key={q.QuestionID} variant="outlined" sx={questionCardSx(i)}>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                    Q{i + 1}{" "}
                    <span style={{ fontWeight: 700, color: "rgba(0,0,0,0.80)" }}>
                      {q.QuestionText}
                    </span>{" "}
                    {q.IsRequired ? (
                      <span style={{ color: THEME_ORANGE, fontWeight: 900 }}>*</span>
                    ) : null}
                  </Typography>

                  <Divider sx={{ my: 1.25, borderColor: "rgba(25,118,210,0.12)" }} />

                  {q.QuestionType === "rating" && (
                    <Rating
                      sx={ratingSx}
                      value={Number(answers[q.QuestionID] || 0)}
                      onChange={(_, v) => setAnswer(q.QuestionID, v || 0)}
                    />
                  )}

                  {q.QuestionType === "mcq" && (
                    <FormControl>
                      <FormLabel sx={{ display: "none" }}>Options</FormLabel>
                      <RadioGroup
                        value={answers[q.QuestionID] || ""}
                        onChange={(e) => setAnswer(q.QuestionID, e.target.value)}
                        sx={{
                          "& .MuiFormControlLabel-root": { my: 0.25 },
                          "& .MuiRadio-root.Mui-checked": { color: THEME_ORANGE },
                        }}
                      >
                        {(q.Options || []).map((o) => (
                          <FormControlLabel
                            key={o.OptionID}
                            value={String(o.OptionID)}
                            control={<Radio />}
                            label={o.OptionText}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}

                  {q.QuestionType === "text" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Type your answer..."
                      value={answers[q.QuestionID] || ""}
                      onChange={(e) => setAnswer(q.QuestionID, e.target.value)}
                      sx={{ ...inputSx, mt: 0.5 }}
                    />
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>

          {/* Additional details + anon */}
          <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Additional Details
            </Typography>

            <TextField
              fullWidth
              label="Further details (optional)"
              multiline
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              sx={{ ...inputSx, mb: 1.5 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  sx={{
                    "&.Mui-checked": { color: THEME_ORANGE },
                  }}
                />
              }
              label="Submit anonymously"
              sx={{ "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button variant="contained" onClick={submit} sx={primaryBtnSx}>
                Submit Feedback
              </Button>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
